// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { ApiException } from '@digitalasset/validator-scan-proxy-api-ts-client';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { decodeToken, fetchPasswordGrantToken, generateToken } from '../utils/auth';
import { useIdpInfo } from './IdpContext';
import { LoggedInUser, UserContext } from './UserContext';

export const UserProvider = ({ children }: PropsWithChildren) => {
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser>();
  const oidcAuth = useAuth();
  const { idpInfo, unsetIdpInfo, setIdpError } = useIdpInfo();
  const nav = useNavigate();
  const location = useLocation();

  // Redirect after login
  useEffect(() => {
    if (loggedInUser && location.pathname.includes('/login')) {
      nav('/');
    }
  }, [loggedInUser, location, nav]);

  useEffect(() => {
    if (idpInfo?.algorithm === 'rs-256' && oidcAuth && oidcAuth.isAuthenticated && oidcAuth.user) {
      const userId = decodeToken(oidcAuth.user.access_token).sub!;
      setLoggedInUser({
        userId,
        userAccessToken: oidcAuth.user.access_token,
      });
    }
  }, [idpInfo, oidcAuth]);

  const login = useCallback(
    async (userId?: string) => {
      if (idpInfo?.algorithm === 'hs-256-unsafe') {
        if (!userId) {
          throw new Error("User ID required for 'HS256' login");
        }
        const userAccessToken = await generateToken(
          userId,
          idpInfo.secret,
          idpInfo.audience,
          idpInfo.scope,
        );
        setLoggedInUser({ userId, userAccessToken });
      } else if (idpInfo?.algorithm === 'rs-256-password-grant') {
        if (!userId) {
          throw new Error("User ID required for 'rs-256-password-grant' login");
        }
        const userAccessToken = await fetchPasswordGrantToken(
          idpInfo.authority,
          idpInfo.clientId,
          userId,
        );
        setLoggedInUser({ userId, userAccessToken });
      } else if (
        idpInfo?.algorithm === 'rs-256' &&
        !hasAuthParams() &&
        !oidcAuth.isAuthenticated &&
        !oidcAuth.activeNavigator &&
        !oidcAuth.isLoading
      ) {
        console.debug('User is unauthenticated. Signing in.');

        await oidcAuth.signinRedirect();
        if (oidcAuth.error) {
          unsetIdpInfo();
          setIdpError(
            'There is an error with the identity provider configuration, please try again or contact your administrator.',
          );
        }
      } else {
        console.debug('Authentication process is still executing.');
      }
    },
    [idpInfo, setIdpError, unsetIdpInfo, oidcAuth],
  );

  const logout = useCallback(async () => {
    setLoggedInUser(undefined);
    if (idpInfo?.algorithm === 'rs-256' && oidcAuth) {
      unsetIdpInfo();
      await oidcAuth.signoutRedirect({
        id_token_hint: oidcAuth.user?.id_token,
        post_logout_redirect_uri: window.location.origin,
      });
    }
  }, [idpInfo, oidcAuth, unsetIdpInfo]);

  const isApiException = (error: unknown): error is ApiException<unknown> => {
    return error !== null && typeof error === 'object' && 'code' in error;
  };

  const signoutOnUnauthorized = useCallback(
    (err: unknown) => {
      if (isApiException(err) && err.code === 401) {
        logout().catch(() => console.debug('There was an issue redirecting the user to logout.'));
      }
    },
    [logout],
  );

  const promptsForUserId = useCallback(
    () => idpInfo?.algorithm === 'hs-256-unsafe' || idpInfo?.algorithm === 'rs-256-password-grant',
    [idpInfo],
  );

  const userState = useMemo(
    () => ({
      loggedInUser,
      login,
      logout,
      promptsForUserId,
      signoutOnUnauthorized,
    }),
    [loggedInUser, login, logout, promptsForUserId, signoutOnUnauthorized],
  );

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
};
